import { Dayjs } from "dayjs";
import {
  IncomeExpenseReportFigures,
  IncomeExpenseReportRow,
} from "../../types/income-expense-report.type";
import { SystemSetting } from "../../types/system-setting.type";
import { findNumberSetting } from "../../utils/hooks/useSystemSettings";
import { roundHalfUp } from "../../utils/money";
import { DAY_TREND_LENGTH, REPORT_MAX_DAYS, THRESHOLD_SETTING_KEY } from "./constants";
import { thresholdText } from "./reportFormat";

type Figures = IncomeExpenseReportFigures;

const PERCENT_SCALE = 100;

/** A row's route-days summed before rounding — the server builder's accumulator. */
interface Accumulator {
  route_day_count: number;
  sales: number;
  cost: number;
  waste_cost: number;
  bills: number;
  target: number | null;
  result: number | null;
  sales_expense: number | null;
  manager_expense: number | null;
  fuel_debit: number;
  fuel_credit: number;
  missing_expense_count: number;
}

const EMPTY_ACCUMULATOR: Accumulator = {
  route_day_count: 0,
  sales: 0,
  cost: 0,
  waste_cost: 0,
  bills: 0,
  target: null,
  result: null,
  sales_expense: null,
  manager_expense: null,
  fuel_debit: 0,
  fuel_credit: 0,
  missing_expense_count: 0,
};

const addNullable = (total: number | null, value: number | null): number | null =>
  value === null ? total : (total ?? 0) + value;

const moneyOrNull = (value: number | null): number | null =>
  value === null ? null : roundHalfUp(value);

const percentOf = (part: number | null, whole: number): number | null =>
  part === null || whole === 0 ? null : roundHalfUp((part / whole) * PERCENT_SCALE);

const accumulate = (total: Accumulator, day: Figures): Accumulator => ({
  route_day_count: total.route_day_count + day.route_day_count,
  sales: total.sales + day.sales,
  cost: total.cost + day.cost,
  waste_cost: total.waste_cost + day.waste_cost,
  bills: total.bills + day.bills,
  target: addNullable(total.target, day.target),
  result: addNullable(total.result, day.result),
  sales_expense: addNullable(total.sales_expense, day.sales_expense),
  manager_expense: addNullable(total.manager_expense, day.manager_expense),
  fuel_debit: total.fuel_debit + day.fuel_debit,
  fuel_credit: total.fuel_credit + day.fuel_credit,
  missing_expense_count: total.missing_expense_count + day.missing_expense_count,
});

/** The server builder's `figuresOf`: the same sums, rounded half-up at the same steps. */
const figuresOf = (total: Accumulator): Figures => {
  const sales = roundHalfUp(total.sales);
  const cost = roundHalfUp(total.cost);
  const wasteCost = roundHalfUp(total.waste_cost);
  const totalCost = roundHalfUp(cost + wasteCost);
  const grossProfit = roundHalfUp(sales - totalCost);
  const salesExpense = moneyOrNull(total.sales_expense);
  const managerExpense = moneyOrNull(total.manager_expense);
  const expenseTotal =
    salesExpense !== null && managerExpense !== null
      ? roundHalfUp(salesExpense + managerExpense)
      : null;
  const profit = expenseTotal === null ? null : roundHalfUp(grossProfit - expenseTotal);
  const fuelAmount = roundHalfUp(total.fuel_debit + total.fuel_credit);
  return {
    route_day_count: total.route_day_count,
    sales,
    cost,
    waste_cost: wasteCost,
    total_cost: totalCost,
    waste_percent: percentOf(wasteCost, totalCost),
    gross_profit: grossProfit,
    gross_profit_percent: percentOf(grossProfit, sales),
    target: moneyOrNull(total.target),
    result: moneyOrNull(total.result),
    sales_expense: salesExpense,
    manager_expense: managerExpense,
    expense_total: expenseTotal,
    expense_percent: percentOf(expenseTotal, sales),
    profit,
    profit_percent: percentOf(profit, sales),
    fuel_debit: roundHalfUp(total.fuel_debit),
    fuel_credit: roundHalfUp(total.fuel_credit),
    fuel_amount: fuelAmount,
    fuel_percent: percentOf(fuelAmount, sales),
    bills: total.bills,
    bills_per_route_day:
      total.route_day_count === 0 ? 0 : roundHalfUp(total.bills / total.route_day_count),
    missing_expense_count: total.missing_expense_count,
  };
};

/**
 * Route-day rows summed into one row, exactly as the server sums them for its branch / route
 * levels (each route-day figure is already at 2 dp, so the sums and every % come out the same).
 */
export const sumFigures = (routeDays: readonly Figures[]): Figures =>
  figuresOf(routeDays.reduce(accumulate, EMPTY_ACCUMULATOR));

// ---- drill-down: ทุกสาขา → สาขา → สาย → วัน ----

export type ViewLevel = "branch" | "route" | "day";

/** Where the reader has drilled to; null = the level above is shown. */
export interface Drill {
  branchCode: string | null;
  routeUuid: string | null;
}

export interface ReportLine {
  key: string;
  label: string;
  branchCode: string;
  routeUuid: string | null;
  businessDate: string | null;
  /** วันทำงาน: the days the line's routes really ran. */
  workDayCount: number;
  figures: Figures;
  /** Profit per day, oldest first; days without a profit figure are left out. */
  profitTrend: number[];
}

export interface ReportView {
  level: ViewLevel;
  lines: ReportLine[];
  totals: Figures;
  totalWorkDayCount: number;
}

const dateOf = (row: IncomeExpenseReportRow): string => row.business_date ?? "";
const routeOf = (row: IncomeExpenseReportRow): string => row.route_uuid ?? "";

const groupBy = <T>(items: readonly T[], keyOf: (item: T) => string): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  });
  return groups;
};

const workDayCountOf = (rows: readonly IncomeExpenseReportRow[]): number =>
  new Set(rows.map(dateOf)).size;

const knownProfits = (figures: readonly Figures[]): number[] =>
  figures.flatMap((item) => (item.profit === null ? [] : [item.profit]));

const dailyProfitsOf = (rows: readonly IncomeExpenseReportRow[]): number[] =>
  knownProfits(
    Array.from(groupBy(rows, dateOf).entries())
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([, dayRows]) => sumFigures(dayRows)),
  );

const labelOf = (code: string, name: string | null): string => (name ? `${code} ${name}` : code);

export const routeLabelOf = (row: IncomeExpenseReportRow): string =>
  labelOf(row.route_code ?? "", row.route_name);

const groupLineOf = (
  key: string,
  label: string,
  rows: IncomeExpenseReportRow[],
  routeUuid: string | null,
): ReportLine => ({
  key,
  label,
  branchCode: rows[0].branch_code,
  routeUuid,
  businessDate: null,
  workDayCount: workDayCountOf(rows),
  figures: sumFigures(rows),
  profitTrend: dailyProfitsOf(rows),
});

const branchLinesOf = (rows: IncomeExpenseReportRow[]): ReportLine[] =>
  Array.from(groupBy(rows, (row) => row.branch_code).entries()).map(([branchCode, branchRows]) =>
    groupLineOf(branchCode, labelOf(branchCode, branchRows[0].branch_name), branchRows, null),
  );

const routeLinesOf = (rows: IncomeExpenseReportRow[]): ReportLine[] =>
  Array.from(groupBy(rows, routeOf).entries()).map(([routeUuid, routeRows]) =>
    groupLineOf(routeUuid, routeLabelOf(routeRows[0]), routeRows, routeUuid),
  );

/** One line per day; its sparkline is its profit and the days before it. */
const dayLinesOf = (rows: IncomeExpenseReportRow[]): ReportLine[] =>
  rows.map((row, index) => ({
    key: `${routeOf(row)}|${dateOf(row)}`,
    label: routeLabelOf(row),
    branchCode: row.branch_code,
    routeUuid: row.route_uuid,
    businessDate: row.business_date,
    workDayCount: 1,
    figures: row,
    profitTrend: knownProfits(rows.slice(Math.max(0, index + 1 - DAY_TREND_LENGTH), index + 1)),
  }));

/**
 * The table for the drill: every branch, the routes of one branch, or the days of one route —
 * all from the route-day rows of one answer. The top level keeps the server's own totals.
 */
export const viewOf = (
  rows: IncomeExpenseReportRow[],
  serverTotals: Figures,
  drill: Drill,
): ReportView => {
  if (drill.branchCode === null) {
    return {
      level: "branch",
      lines: branchLinesOf(rows),
      totals: serverTotals,
      totalWorkDayCount: workDayCountOf(rows),
    };
  }
  const branchRows = rows.filter((row) => row.branch_code === drill.branchCode);
  if (drill.routeUuid === null) {
    return {
      level: "route",
      lines: routeLinesOf(branchRows),
      totals: sumFigures(branchRows),
      totalWorkDayCount: workDayCountOf(branchRows),
    };
  }
  const routeRows = branchRows
    .filter((row) => row.route_uuid === drill.routeUuid)
    .sort((rowA, rowB) => dateOf(rowA).localeCompare(dateOf(rowB)));
  return {
    level: "day",
    lines: dayLinesOf(routeRows),
    totals: sumFigures(routeRows),
    totalWorkDayCount: workDayCountOf(routeRows),
  };
};

export const isRangeTooLong = (dateFrom: Dayjs, dateTo: Dayjs): boolean =>
  dateTo.startOf("day").diff(dateFrom.startOf("day"), "day") + 1 > REPORT_MAX_DAYS;

// ---- เกณฑ์สี (ตั้งค่าระบบ) ----

/** แดง = เกินเกณฑ์ / ขาดทุน · เหลือง = เฝ้าระวัง · เขียว = ตามเกณฑ์. */
export type Tone = "good" | "warn" | "bad";

export type Thresholds = Record<keyof typeof THRESHOLD_SETTING_KEY, number>;

/** The nine settings; null until every one is read (no colour rather than a guessed one). */
export const thresholdsOf = (settings: SystemSetting[]): Thresholds | null => {
  const valueOf = (key: string) => findNumberSetting(settings, key);
  const wasteGoodMax = valueOf(THRESHOLD_SETTING_KEY.wasteGoodMax);
  const wasteWarnMax = valueOf(THRESHOLD_SETTING_KEY.wasteWarnMax);
  const expenseGoodMax = valueOf(THRESHOLD_SETTING_KEY.expenseGoodMax);
  const expenseWarnMax = valueOf(THRESHOLD_SETTING_KEY.expenseWarnMax);
  const profitGoodMin = valueOf(THRESHOLD_SETTING_KEY.profitGoodMin);
  const profitWarnMin = valueOf(THRESHOLD_SETTING_KEY.profitWarnMin);
  const fuelGoodBelow = valueOf(THRESHOLD_SETTING_KEY.fuelGoodBelow);
  const fuelWarnMax = valueOf(THRESHOLD_SETTING_KEY.fuelWarnMax);
  const billsWatchMin = valueOf(THRESHOLD_SETTING_KEY.billsWatchMin);
  if (
    wasteGoodMax === undefined ||
    wasteWarnMax === undefined ||
    expenseGoodMax === undefined ||
    expenseWarnMax === undefined ||
    profitGoodMin === undefined ||
    profitWarnMin === undefined ||
    fuelGoodBelow === undefined ||
    fuelWarnMax === undefined ||
    billsWatchMin === undefined
  ) {
    return null;
  }
  return {
    wasteGoodMax,
    wasteWarnMax,
    expenseGoodMax,
    expenseWarnMax,
    profitGoodMin,
    profitWarnMin,
    fuelGoodBelow,
    fuelWarnMax,
    billsWatchMin,
  };
};

/** Lower is better: up to `good` green, up to `warn` yellow, above red. */
const toneAtMost = (value: number, good: number, warn: number): Tone => {
  if (value <= good) return "good";
  return value <= warn ? "warn" : "bad";
};

export interface FigureTones {
  result: Tone | null;
  waste: Tone | null;
  expense: Tone | null;
  profit: Tone | null;
  fuel: Tone | null;
  bills: Tone | null;
}

const resultToneOf = (result: number | null): Tone | null => {
  if (result === null) return null;
  return result >= 0 ? "good" : "bad";
};

const profitToneOf = (percent: number | null, thresholds: Thresholds): Tone | null => {
  if (percent === null) return null;
  if (percent >= thresholds.profitGoodMin) return "good";
  return percent >= thresholds.profitWarnMin ? "warn" : "bad";
};

const fuelToneOf = (percent: number | null, thresholds: Thresholds): Tone | null => {
  if (percent === null) return null;
  if (percent < thresholds.fuelGoodBelow) return "good";
  return percent <= thresholds.fuelWarnMax ? "warn" : "bad";
};

/**
 * The colour of each graded cell, compared on the unrounded figure. บิลเฉลี่ย is only flagged
 * (yellow, เฝ้าระวัง) from its threshold up, as the approved prototype draws it.
 */
export const tonesOf = (figures: Figures, thresholds: Thresholds | null): FigureTones => {
  const result = resultToneOf(figures.result);
  if (thresholds === null) {
    return { result, waste: null, expense: null, profit: null, fuel: null, bills: null };
  }
  const { waste_percent: waste, expense_percent: expense } = figures;
  return {
    result,
    waste:
      waste === null ? null : toneAtMost(waste, thresholds.wasteGoodMax, thresholds.wasteWarnMax),
    expense:
      expense === null
        ? null
        : toneAtMost(expense, thresholds.expenseGoodMax, thresholds.expenseWarnMax),
    profit: profitToneOf(figures.profit_percent, thresholds),
    fuel: fuelToneOf(figures.fuel_percent, thresholds),
    bills: figures.bills_per_route_day >= thresholds.billsWatchMin ? "warn" : null,
  };
};

/** The legend's line, from the live settings. */
export const legendTextOf = (thresholds: Thresholds): string => {
  const text = thresholdText;
  return [
    `ต้นทุนเสีย ≤${text(thresholds.wasteGoodMax)} / ≤${text(thresholds.wasteWarnMax)}%`,
    `ค่าใช้จ่าย ≤${text(thresholds.expenseGoodMax)} / ≤${text(thresholds.expenseWarnMax)}%`,
    `กำไร ≥${text(thresholds.profitGoodMin)} / ≥${text(thresholds.profitWarnMin)}%`,
    `น้ำมัน <${text(thresholds.fuelGoodBelow)} / ≤${text(thresholds.fuelWarnMax)}%`,
    `บิลเฉลี่ย ≥${text(thresholds.billsWatchMin)} เฝ้าระวัง`,
  ].join(" · ");
};

/** The expense cell's warning dot (actual mode): why, or null when every day is approved. */
export const missingDocumentTipOf = (figures: Figures): string | null => {
  if (figures.missing_expense_count === 0) return null;
  return figures.route_day_count === 1
    ? "ยังไม่มีเอกสารรายวันที่อนุมัติ · ยังไม่รวมในยอดจริง"
    : `มี ${figures.missing_expense_count} สาย-วัน ที่ยังไม่มีเอกสารรายวันที่อนุมัติ`;
};
